import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import Stories from 'react-insta-stories';
import { useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { HomeStoryMediaItem, StoreConfig } from '../services/backend';

interface FloatingLiveStoriesButtonProps {
  config?: StoreConfig | null;
}

const LIVE_ICON_URL = '/media/veraz/stories/live-icon.png';

const FloatingLiveStoriesButton: React.FC<FloatingLiveStoriesButtonProps> = ({ config }) => {
  const location = useLocation();
  const { cart } = useCart();
  const storiesSection = config?.homeSections?.stories;
  const [items, setItems] = useState<HomeStoryMediaItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const shouldShowOnRoute =
    location.pathname === '/' ||
    location.pathname.startsWith('/produtos') ||
    location.pathname.startsWith('/produto/');

  useEffect(() => {
    let mounted = true;

    if (!config || storiesSection?.enabled === false) {
      setItems([]);
      return () => {
        mounted = false;
      };
    }

    const adminItems =
      storiesSection?.source === 'admin' && Array.isArray(storiesSection?.items)
        ? storiesSection.items
        : null;

    if (adminItems) {
      setItems(
        adminItems
          .filter((item) => item?.url && item?.type && item.enabled !== false)
          .sort((left, right) => left.order - right.order)
      );
      return () => {
        mounted = false;
      };
    }

    const loadManifest = async () => {
      try {
        const response = await fetch('/generated/veraz-stories.json', { cache: 'no-store' });
        if (!response.ok) {
          if (mounted) setItems([]);
          return;
        }

        const payload = await response.json();
        if (!mounted) return;

        const nextItems = Array.isArray(payload?.items)
          ? payload.items
              .filter(
                (item: HomeStoryMediaItem) =>
                  item?.url && item?.type && item.enabled !== false
              )
              .sort((left: HomeStoryMediaItem, right: HomeStoryMediaItem) => left.order - right.order)
          : [];

        setItems(nextItems);
      } catch (error) {
        console.error('Erro ao carregar manifesto de lives:', error);
        if (mounted) setItems([]);
      }
    };

    loadManifest();

    return () => {
      mounted = false;
    };
  }, [config, storiesSection?.enabled, storiesSection?.items, storiesSection?.source]);

  const videoItems = useMemo(() => items.filter((item) => item.type === 'video'), [items]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isOpen]);

  const videoStories = useMemo(
    () =>
      videoItems.map((item) => ({
        url: item.url,
        type: 'video',
        muted: false,
        preloadResource: true,
        styles: {
          height: '100%',
          objectFit: 'cover',
          width: '100%',
        },
      })),
    [videoItems]
  );

  const hasFloatingCart =
    cart.length > 0 &&
    location.pathname !== '/carrinho' &&
    !location.pathname.startsWith('/checkout');
  const bottomClass = hasFloatingCart ? 'bottom-28 md:bottom-24' : 'bottom-5 md:bottom-6';
  const launcherStyle = isOpen
    ? ({
        bottom: 'max(18px, calc((100vh - min(88vh, 780px)) / 2 + 10px))',
        left: 'calc(50% - min(264px, 42vw))',
      } as React.CSSProperties)
    : undefined;
  const launcherPositionClass = isOpen ? 'z-[150]' : `left-4 ${bottomClass} z-[90] md:left-6`;
  const launcherSizeClass = isOpen ? 'h-16 w-16' : 'h-[74px] w-[74px]';

  useEffect(() => {
    if (!config || !shouldShowOnRoute || storiesSection?.enabled === false || videoItems.length === 0) {
      setShowPrompt(false);
      return undefined;
    }

    let showTimer: number | undefined;
    let hideTimer: number | undefined;

    const schedulePrompt = (delay: number) => {
      showTimer = window.setTimeout(() => {
        setShowPrompt(true);

        hideTimer = window.setTimeout(() => {
          setShowPrompt(false);
          schedulePrompt(10000);
        }, 5000);
      }, delay);
    };

    setShowPrompt(false);
    schedulePrompt(7000);

    return () => {
      if (showTimer) window.clearTimeout(showTimer);
      if (hideTimer) window.clearTimeout(hideTimer);
    };
  }, [config, location.pathname, shouldShowOnRoute, storiesSection?.enabled, videoItems.length]);

  if (!config || !shouldShowOnRoute || storiesSection?.enabled === false || videoItems.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className={`group fixed ${launcherPositionClass} flex ${launcherSizeClass} items-center justify-center transition-all duration-300 ease-out`}
        style={launcherStyle}
      >
        {!isOpen ? (
          <span
            className={`pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-[11px] font-bold uppercase leading-none tracking-[0.08em] text-veraz-pink shadow-[0_14px_30px_rgba(35,31,32,0.16)] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-x-[7px] after:border-t-[8px] after:border-x-transparent after:border-t-white group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100 group-hover:blur-0 ${
              showPrompt
                ? 'translate-y-0 scale-100 opacity-100 blur-0'
                : 'translate-y-2 scale-95 opacity-0 blur-sm'
            }`}
          >
            Assistir
          </span>
        ) : null}

        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className={`flex ${launcherSizeClass} items-center justify-center rounded-full bg-[conic-gradient(from_210deg,#f6c400_0deg,#ff7a00_55deg,#e6007e_150deg,#c02df2_260deg,#f6c400_360deg)] p-[3px] shadow-[0_18px_40px_rgba(230,0,126,0.22)] transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-veraz-pink/20`}
          aria-label="Abrir videos Live da Veraz"
        >
          <span className="flex h-full w-full items-center justify-center rounded-full bg-white p-[3px]">
            <img
              src={LIVE_ICON_URL}
              alt=""
              className="h-full w-full rounded-full object-cover"
              draggable={false}
            />
          </span>
        </button>
      </div>

      <AnimatePresence>
        {isOpen && videoStories.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] bg-black/96 backdrop-blur-[2px]"
          >
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="fixed right-4 top-4 z-[10000] flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#111111] shadow-[0_18px_36px_rgba(0,0,0,0.32)] hover:bg-[#f5f5f5] md:right-5 md:top-5"
              aria-label="Fechar videos Live"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex h-full w-full items-center justify-center p-3 md:p-8">
              <div className="relative h-[min(88vh,780px)] w-full max-w-[430px] overflow-visible">
                <div className="h-full overflow-hidden rounded-[30px] bg-black shadow-[0_30px_80px_rgba(0,0,0,0.55)] ring-1 ring-white/10">
                  <Stories
                    stories={videoStories}
                    width="100%"
                    height="100%"
                    loop={false}
                    keyboardNavigation
                    onAllStoriesEnd={() => setIsOpen(false)}
                    storyContainerStyles={{
                      background: '#000000',
                      height: '100%',
                      width: '100%',
                    }}
                    progressContainerStyles={{
                      paddingTop: 12,
                      paddingLeft: 12,
                      paddingRight: 62,
                      boxSizing: 'border-box',
                    }}
                    progressStyles={{
                      background: '#e6007e',
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default FloatingLiveStoriesButton;
